import React, { useState, useContext, useEffect } from 'react';
import { I18nReactContext } from '../../../../../contexts/I18nContext';
import { CourseEditorContext } from '../../../../../contexts/CourseEditorContext';
import { getCompanyImageBankAPI } from '../../../../../Services/api';
import { getPublicAssetsAPI } from '../../../../../Services/PublicAssets_api';
import ImageIcon from '@mui/icons-material/Image';
import './ChapterEditor.scss';

export const ImageComponent = ({
    id,
    content,
    signedUrl, // Signed URL from server - preferred for display
    isSelected,
    onSelect,
    borderRadius,
    backgroundColor,
    opacity = 1,
    mode = "edit",
    isTemplateMode = false, // New prop to check if we're in super admin mode
    onAnimationComplete,
    isSequentialAnimationActive = false,
    onOpenEditorDrawer
}) => {
    const { i18n } = useContext(I18nReactContext);
    const courseEditorContext = useContext(CourseEditorContext);
    const [isAIAnimating, setIsAIAnimating] = useState(false);

    // This temporary URL is used to display the image immediately after upload
    // while still storing the CompanyFile ID in the content attribute.
    // The server will convert the ID to a signed URL when it returns the course data.
    const [tempImageUrl, setTempImageUrl] = useState(null);


    // Check if content is an image key and try to load it from the image bank
    // Skip if signedUrl is already provided from server
    useEffect(() => {
        // Only proceed if content exists, is a string, is not a URL, and signedUrl is not provided
        if (content && typeof content === 'string' && !content.startsWith('http') && !signedUrl) {
            console.log("Content appears to be an image key, trying to load from image bank:", content);

            const loadImageFromBank = async () => {
                try {
                    if (isTemplateMode) {
                        // In template mode, use the public assets API
                        console.log("Loading public image from bank for template mode with key:", content);
                        const response = await getPublicAssetsAPI({
                            paginationModel: { page: 0, pageSize: 5 },
                            filterModel: {
                                filterValues: {
                                    type: 'IMAGE'
                                }
                            },
                            sortModel: { field: 'createdAt', sort: 'desc' }
                        });

                        // Find the image with the matching key
                        if (response && response.data) {
                            const image = response.data.find(asset => asset.id === content);
                            if (image && image.signedUrl) {
                                console.log("Found image in public bank with matching key, using signed URL:", image.signedUrl);
                                setTempImageUrl(image.signedUrl);
                            } else {
                                // Clear tempImageUrl if the image is not found
                                setTempImageUrl(null);
                            }
                        } else {
                            // Clear tempImageUrl if no data is returned
                            setTempImageUrl(null);
                        }
                    } else {
                        // In regular mode, use the company image bank API
                        const images = await getCompanyImageBankAPI();
                        const image = images.find(img => img.key === content);
                        if (image && image.signedUrl) {
                            console.log("Found image in company bank with matching key, using signed URL:", image.signedUrl);
                            setTempImageUrl(image.signedUrl);
                        } else {
                            // Clear tempImageUrl if the image is not found
                            setTempImageUrl(null);
                        }
                    }
                } catch (error) {
                    console.error("Error loading image from bank:", error);
                    // Clear tempImageUrl on error
                    setTempImageUrl(null);
                }
            };

            loadImageFromBank();
        } else if (!content || content.startsWith('http') || signedUrl) {
            // Clear tempImageUrl if content is empty, is already a URL, or signedUrl is provided
            setTempImageUrl(null);
        }
    }, [content, isTemplateMode, signedUrl]);

    // Only clear the temporary URL when the component is unmounted
    // We don't want to clear it when content changes, as we need it for displaying images from the image bank
    useEffect(() => {
        // Clean up function to clear the temporary URL when the component is unmounted
        return () => {
            setTempImageUrl(null);
        };
    }, []);

    // AI Image Creation Animation Effect
    useEffect(() => {
        const isAIGenerating = courseEditorContext?.isGeneratingChapter;
        const isShowingAIAnimations = courseEditorContext?.isShowingAIChapterAnimations;
        const hasContent = content && (content.startsWith('http') || tempImageUrl);

        // Only animate if (AI is generating OR showing AI chapter animations OR sequential animations are active) and we have new image content and we're not in edit mode
        if ((isAIGenerating || isShowingAIAnimations || isSequentialAnimationActive) && hasContent && mode !== "edit") {
            setIsAIAnimating(true);

            // For sequential animations during AI generation, trigger completion almost immediately
            // For other cases, use the full 2-second animation duration
            const completionDelay = isSequentialAnimationActive ? 200 : 2000;

            // Stop animation after the appropriate delay
            const animationTimeout = setTimeout(() => {
                setIsAIAnimating(false);
                // Call animation completion callback for sequential animations
                if (isSequentialAnimationActive && onAnimationComplete) {
                    console.log('ImageComponent animation completed for:', id);
                    onAnimationComplete(id);
                }
            }, completionDelay);

            return () => {
                clearTimeout(animationTimeout);
            };
        } else {
            setIsAIAnimating(false);
            // If sequential animations are active but we're not showing animation,
            // immediately call completion callback (even without content to prevent sequence from getting stuck)
            if (isSequentialAnimationActive && onAnimationComplete) {
                console.log('ImageComponent immediately completing for:', id);
                setTimeout(() => onAnimationComplete(id), 100);
            }
        }
    }, [content, tempImageUrl, courseEditorContext?.isGeneratingChapter, courseEditorContext?.isShowingAIChapterAnimations, mode, isSequentialAnimationActive, onAnimationComplete, id]);


    const handleClick = (e) => {
        e.stopPropagation();
        if (!isSelected) {
            onSelect(id);
        }
    };

    // Double-click opens the editor drawer
    const handleDoubleClick = (e) => {
        e.stopPropagation();
        if (mode === "edit" && onOpenEditorDrawer) {
            onOpenEditorDrawer({
                id,
                type: 'image',
                content,
                borderRadius,
                backgroundColor,
                opacity
            });
        }
    };





    // Parse border radius values or use defaults
    const topLeft = borderRadius?.topLeft || 0;
    const topRight = borderRadius?.topRight || 0;
    const bottomLeft = borderRadius?.bottomLeft || 0;
    const bottomRight = borderRadius?.bottomRight || 0;

    // Create style object for the image container
    const containerStyle = {
        width: '100%',
        height: '100%',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        overflow: 'hidden',
        position: 'relative',
        borderRadius: `${topLeft}px ${topRight}px ${bottomRight}px ${bottomLeft}px`,
    };

    // Create style for the background with opacity
    const backgroundStyle = {
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: backgroundColor || 'transparent',
        opacity: opacity,
        borderRadius: `${topLeft}px ${topRight}px ${bottomRight}px ${bottomLeft}px`,
        zIndex: -1, // Ensure background is behind the image content
    };

    // Create style object for the image
    const imageStyle = {
        width: '100%',
        height: '100%',
        objectFit: 'fill',  // Allow free resizing without preserving aspect ratio
        opacity: opacity
    };

    return (
        <div 
            className={`ImageComponent ${isSelected ? 'selected' : ''}`}
            onClick={handleClick}
            onDoubleClick={handleDoubleClick}
        >
            <div className={`image-container ${isAIAnimating ? 'ai-image-creation' : ''}`} style={containerStyle}>
                {/* Background div with opacity */}
                <div style={backgroundStyle}></div>

                {/* Image or placeholder */}
                {content ? (
                    <img
                        src={
                            // Priority 1: Use signedUrl from server (fresh signed URL)
                            signedUrl ? signedUrl :
                            // Priority 2: Use tempImageUrl (for immediate display after upload)
                            tempImageUrl ? tempImageUrl :
                            // Priority 3: If content is a URL (legacy support), use it
                            (typeof content === 'string' && content.startsWith('http')) ? content :
                            // Fallback: Content is an ID, use placeholder while loading from bank
                            'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgZmlsbD0iI2YwZjBmMCIvPjx0ZXh0IHg9IjUwJSIgeT0iNTAlIiBmb250LWZhbWlseT0iQXJpYWwiIGZvbnQtc2l6ZT0iMjAiIHRleHQtYW5jaG9yPSJtaWRkbGUiIGZpbGw9IiM5OTkiPkltYWdlPC90ZXh0Pjwvc3ZnPg=='
                        } 
                        alt={i18n.translate('weezbeeCourse.editor.imageComponent.altText', { defaultValue: 'Image' })} 
                        style={imageStyle}
                        onError={(e) => {
                            console.error("Image failed to load with content:", content);

                            // If the image fails to load and content is not a URL, it might be a companyFile id
                            // In this case, we should try to load the image from the company image bank
                            if (!content.startsWith('http')) {
                                console.log("Content is not a URL, likely an image ID:", content);
                                // Try to load the image from the company image bank
                                const loadImageFromBank = async () => {
                                    try {
                                        const images = await getCompanyImageBankAPI();
                                        const image = images.find(img => img.key === content);
                                        if (image && image.signedUrl) {
                                            console.log("Found image in bank with matching key, using signed URL:", image.signedUrl);
                                            e.target.src = image.signedUrl;
                                            // Store the signed URL in tempImageUrl for future reference
                                            setTempImageUrl(image.signedUrl);
                                            return;
                                        }
                                    } catch (error) {
                                        console.error("Error loading image from bank:", error);
                                    }
                                    // If we couldn't find the image or there was an error, use the placeholder
                                    e.target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgZmlsbD0iI2YwZjBmMCIvPjx0ZXh0IHg9IjUwJSIgeT0iNTAlIiBmb250LWZhbWlseT0iQXJpYWwiIGZvbnQtc2l6ZT0iMjAiIHRleHQtYW5jaG9yPSJtaWRkbGUiIGZpbGw9IiM5OTkiPkltYWdlPC90ZXh0Pjwvc3ZnPg==';
                                };
                                loadImageFromBank();
                            } else {
                                // If content is a URL but still failed to load, use the placeholder
                                e.target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgZmlsbD0iI2YwZjBmMCIvPjx0ZXh0IHg9IjUwJSIgeT0iNTAlIiBmb250LWZhbWlseT0iQXJpYWwiIGZvbnQtc2l6ZT0iMjAiIHRleHQtYW5jaG9yPSJtaWRkbGUiIGZpbGw9IiM5OTkiPkltYWdlPC90ZXh0Pjwvc3ZnPg==';
                            }
                        }}
                    />
                ) : (
                    <div className="image-placeholder">
                        <ImageIcon fontSize="large" />
                        <span>{i18n.translate('weezbeeCourse.editor.imageComponent.placeholder', { defaultValue: 'Click to add image' })}</span>
                    </div>
                )}
            </div>

        </div>
    );
};
